import React, { useEffect, useLayoutEffect, useState } from 'react';
import Linkify from 'react-linkify';
import ReactGA from 'react-ga';

import { IDatabase, IDatabaseField, getAnalyticsToken } from '../../services/database';
import { get as getAttachment, IAttachment } from '../../services/attachment';
import { IOrganization } from '../../services/account';
import { getSepcialPageFieldKeysForDataabse } from '../../helpers/pages';
import { get as getPage, IPage, getOrganization } from '../../services/pages';

import pdf from '../../images/pdf.png';
import doc from '../../images/doc.png';
import eps from '../../images/eps.png';
import gif from '../../images/gif.png';
import jpg from '../../images/jpg.png';
import psd from '../../images/psd.png';
import xls from '../../images/xls.png';
import Modal from '../../shared/Modal';

interface Props {
    database?: IDatabase,
    pageId: string,
    onClose: () => void
}
export default ({ database, pageId, onClose }: Props) => {
    const [attachments, setAttachments] = useState<IAttachment[]>([]);
    const [page, setPage] = useState<IPage | null>(null);
    const [organization, setOrganization] = useState<IOrganization | null>(null);
    const [show, setShow] = useState<boolean>(!!pageId);

    useLayoutEffect(() => {
        if (show) $('#detailModal').modal('show')
        else $('#detailModal').modal('hide')
    })

    useLayoutEffect(() => {
        $('#detailModal').off('hidden.bs.modal').on('hidden.bs.modal', () => {
            setShow(false)
        });
    })

    useEffect(() => {
        if (pageId && !show) onClose()
    }, [show])

    useEffect(() => {
        addTracker();
        loadPage();
        if (pageId) setShow(true)
        else setShow(false)
    }, [pageId, database]);

    const fetchPageAttachments = async (currentPage: IPage) => {
        const att: IAttachment[] = [];
        if (Array.isArray(currentPage.row.map.attachments))
            await Promise.all(
                currentPage.row.map.attachments.map(async (a: string) => {
                    const attachment = await getAttachment(
                        a,
                        typeof currentPage.organization === 'object'
                            ? currentPage.organization.name
                            : currentPage.organization
                    );
                    if (
                        attachment.success &&
                        attachment.data &&
                        !Array.isArray(attachment.data)
                    ) {
                        att.push(attachment.data);
                    }
                })
            );
        setAttachments(att);
    };

    const loadOrganization = async (orgName: string) => {
        const orgResult = await getOrganization(orgName);
        if (orgResult.success && orgResult.organization) {
            setOrganization(orgResult.organization);
        }
    };

    const loadPage = async () => {
        const accountName = typeof database?.account === 'string' ? database?.account : database?.account?.name
        if (!accountName) return
        const pageResult = await getPage(pageId, accountName);
        if (pageResult.success && pageResult.pages) {
            const localPage = pageResult.pages[0];
            await loadOrganization(typeof localPage.organization === 'object'
                ? localPage.organization.name
                : localPage.organization)
            setPage(localPage);
            await fetchPageAttachments(localPage);
        }
    };

    const addTracker = async () => {
        if (!database || !database.name || !page) return;
        try {
            const c = ((await getAnalyticsToken(database.name)) || '').replace(/"/g, '');
            ReactGA.addTrackers([
                {
                    trackingId: c,
                    gaOptions: { name: 'client' },
                },
            ]);
        } catch (ex) {
            console.error(ex);
        }
        const org = typeof page.organization === 'object' && page.organization;
        ReactGA.pageview(
            `${org && org.title}/${page.database && page.database.title}/${page.title}`,
            ['harmonize', 'client']
        );
    };

    const getFileType = (name: string) => {
        const type = (name.split('.').pop() || '').toLocaleLowerCase();
        switch (type) {
            case 'pdf':
                return pdf;
            case 'ppt':
                return doc;
            case 'docx':
                return doc;
            case 'doc':
                return doc;
            case 'png':
                return doc;
            case 'jpg':
                return jpg;
            case 'jpeg':
                return jpg;
            case 'eps':
                return eps;
            case 'gif':
                return gif;
            case 'psd':
                return psd;
            case 'xls':
                return xls;
            case 'xlsx':
                return xls;
            case 'psd':
                return psd;
            default:
                return doc;
        }
    };

    const renderAttachment = (attachment: IAttachment) => (
        <li>
            <div style={{ marginRight: 50 }}>
                <a href={attachment.url} className="attachment-link" target="_blank">
                    <img src={getFileType(attachment.fileName)} className="pb-3" />
                    <p>{attachment.fileName}</p>
                </a>
            </div>
        </li>)

    const renderDetailModalSection = (icon, label, children) => (
        <div style={{ display: 'flex', alignItems: 'start', marginBottom: 20 }}>
            <div style={{ paddingLeft: 20, paddingRight: 10 }}>{icon}</div>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
                <h5>{label}</h5>
                <div>{children}</div>
            </div>
        </div>
    );

    const sortList = (list: string[], databaseField: IDatabaseField) => {
        return (Array.isArray(list) && Array.isArray(databaseField?.options)) ? list.sort((a, b) => databaseField.options.indexOf(a) - databaseField.options.indexOf(b)) : list;
    }

    const filterList = (list: string[], databaseField: IDatabaseField) => {
        return (Array.isArray(list) && Array.isArray(databaseField?.options)) ? list.filter((a) => databaseField.options.indexOf(a) !== -1) : list;
    }

    if (!pageId) return null;

    const detailPageImage = organization?.avatar;

    let modalImage: any = {};

    if (detailPageImage !== '(none)' && detailPageImage) {
        modalImage = {
            backgroundImage: `url("${detailPageImage.replace(/ /g, '%20')}")`,
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
            backgroundSize: '300px 168.75px',
        };
    }

    if (!database || !database.fields || !page) return null;

    const specialFields = getSepcialPageFieldKeysForDataabse(database);

    for (const key in specialFields)
        specialFields[key] = JSON.parse(page?.row?.map?.[specialFields[key]] || null);

    const {
        number: num,
        contact,
        email,
        agesServed,
        fees,
        title,
        description,
        focusArea,
        availableVirtually,
        gradesServed,
        servesSpecialNeeds,
        programAvailability,
        transportationAvailable,
        mealsProvided,
        street,
        city,
        state,
        zip,
        link,
        programDates,
        programDaysOfWeek,
        programTimes,
    } = specialFields as any;

    const actionButtons = [
        <button
            className="btn btn-primary"
            key="detail-modal-done"
            onClick={() => setShow(false)}>
            Done
        </button>,
    ];

    const dbFocusAreas = database?.fields?.find(f => `${f.name}`.includes('program_focus_area_')) as IDatabaseField
    const dbGradesServed = database?.fields?.find(f => `${f.name}`.includes('grades_served')) as IDatabaseField
    const dbAgesServed = database?.fields?.find(f => `${f.name}`.includes('ages_served')) as IDatabaseField
    const dbProgramAvailability = database?.fields?.find(f => `${f.name}`.includes('program_availability')) as IDatabaseField
    const dbDaysOfWeek = database?.fields?.find(f => `${f.name}`.includes('program_days_of_week_')) as IDatabaseField
    const dbMealsProvided = database?.fields?.find(f => `${f.name}`.includes('meals_provided')) as IDatabaseField

    return (
        <Modal
            large
            id={`detailModal`}
            actionButtons={actionButtons}>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
                <div
                    style={{
                        backgroundImage: modalImage.backgroundImage,
                        backgroundSize: 'contain',
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'center',
                        minHeight: 100,
                        width: '100%',
                    }}
                />
                {title && (
                    <div style={{ textAlign: 'center' }} className="pt-3">
                        {title && <h2>{title}</h2>}
                    </div>
                )}
                {organization?.orgName && (
                    <div style={{ textAlign: 'center', color: '#595959' }}>
                        <h5>{organization?.orgName}</h5>
                    </div>
                )}
                {description && <div style={{ padding: 20 }}>{description}</div>}
                {focusArea &&
                    renderDetailModalSection(
                        <em className="fas fa-book-open text-muted" style={{ fontSize: 16 }} />,
                        'Program Focus Area',
                        <div>
                            <ul className="pl-4 pb-0">
                                {sortList(filterList(focusArea, dbFocusAreas), dbFocusAreas)?.map(item => <li key={item}>{item}</li>)}
                            </ul>
                        </div>
                    )}
                {availableVirtually &&
                    renderDetailModalSection(
                        <em className="fas fa-laptop text-muted" style={{ fontSize: 16 }} />,
                        'Is this program available virtually?',
                        <div className="pl-2">{availableVirtually}</div>
                    )}
                {(contact || email || num) &&
                    renderDetailModalSection(
                        <em className="fas fa-address-card text-muted" style={{ fontSize: 16 }} />,
                        'Contact Information',
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <div className="pl-2">
                                {contact}
                                {num && <div><a href={`tel:+1${num.replace(/\D/g, '')}`}>{num}</a></div>}
                                {email && <div><Linkify>{email}</Linkify></div>}
                            </div>
                        </div>
                    )}
                {(street || city || state || zip) &&
                    renderDetailModalSection(
                        <em className="fas fa-map-marker text-muted" style={{ fontSize: 16 }} />,
                        'Program Address',
                        <a target="_blank" href={`https://maps.google.com/?q=${street} ${city}, ${state}, ${zip}`}>
                            <div>{street || ''}</div>
                            <div>{[city, state, zip].filter((x) => x).join(', ')}</div>
                        </a>
                    )}
                {link &&
                    renderDetailModalSection(
                        <em className="fas fa-globe text-muted" style={{ fontSize: 16 }} />,
                        'Organization Website/Program Link',
                        <div className="pl-2">
                            <a target="_blank" href={link?.includes('http') ? link : `http://${link}`}>{link}</a>
                        </div>
                    )}
                {gradesServed &&
                    renderDetailModalSection(
                        <em className="fas fa-graduation-cap text-muted" style={{ fontSize: 16 }} />,
                        'Grades Served',
                        <div>
                            {Array.isArray(gradesServed) ? (
                                <ul className="pl-4 pb-0">
                                    {sortList(filterList(gradesServed, dbGradesServed), dbGradesServed)?.map(item => <li key={item}>{item}</li>)}
                                </ul>
                            ) : (
                                gradesServed
                            )}
                        </div>
                    )}
                {agesServed &&
                    renderDetailModalSection(
                        <em className="fas fa-child text-muted" style={{ fontSize: 16 }} />,
                        'Ages Served',
                        <div>
                            {Array.isArray(agesServed) ? (
                                <ul className="pl-4 pb-0">
                                    {sortList(filterList(agesServed, dbAgesServed), dbAgesServed)?.map(item => <li key={item}>{item}</li>)}
                                </ul>
                            ) : (
                                agesServed
                            )}
                        </div>
                    )}
                {servesSpecialNeeds &&
                    renderDetailModalSection(
                        <em className="fas fa-hand-holding-heart text-muted" style={{ fontSize: 16 }} />,
                        'Does this program serve youth with special needs?',
                        <div className="pl-2">{servesSpecialNeeds}</div>
                    )}
                {(programAvailability || programDates || programDaysOfWeek || programTimes) &&
                    renderDetailModalSection(
                        <em className="fas fa-calendar text-muted" style={{ fontSize: 16 }} />,
                        'Program Availability',
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            {programAvailability && (
                                <div>
                                    <b>Program Availability</b>{' '}
                                    {Array.isArray(programAvailability) ? (
                                        <ul className="pl-4 pb-0">
                                            {sortList(filterList(programAvailability, dbProgramAvailability), dbProgramAvailability)?.map(item => <li key={item}>{item}</li>)}
                                        </ul>
                                    ) : (
                                        programAvailability
                                    )}
                                </div>)}
                            {programDates && (
                                <div>
                                    <b>Program Dates</b>
                                    <div className="pl-2 pb-3">{programDates}</div>
                                </div>
                            )}
                            {programDaysOfWeek && (
                                <div>
                                    <b>Program Days of Week</b>{' '}
                                            {Array.isArray(programDaysOfWeek) ? (
                                                <ul className="pl-4 pb-0">
                                            {sortList(filterList(programDaysOfWeek, dbDaysOfWeek), dbDaysOfWeek)?.map(item => <li key={item}>{item}</li>)}
                                                </ul>
                                            ) : (
                                                programDaysOfWeek
                                            )}
                                </div>
                            )}
                            {programTimes && (
                                <div>
                                    <b>Program Times</b>
                                    <div className="pl-2">{programTimes}</div>
                                </div>
                            )}
                        </div>
                    )}
                {fees &&
                    renderDetailModalSection(
                        <em className="fas fa-dollar-sign text-muted" style={{ fontSize: 16 }} />,
                        'Program Fees',
                        <div>{fees}</div>
                    )}
                {transportationAvailable &&
                    renderDetailModalSection(
                        <em className="fas fa-car text-muted" style={{ fontSize: 16 }} />,
                        'Transportation Available',
                        <div className="pl-2">{transportationAvailable}</div>
                    )}
                {mealsProvided &&
                    renderDetailModalSection(
                        <em className="fas fa-utensils text-muted" style={{ fontSize: 16 }} />,
                        'Meals Provided',
                        <div>
                            {Array.isArray(mealsProvided) ? (
                                <ul className="pl-4 pb-0">
                                    {sortList(filterList(mealsProvided, dbMealsProvided), dbMealsProvided)?.map(item => <li key={item}>{item}</li>)}
                                </ul>
                            ) : (
                                mealsProvided
                            )}
                        </div>
                    )}
                {!!attachments?.length && <section id="attachments" className="py-5">
                    <div className="container">
                        <h4>Attachments</h4>
                        <ul className="pdf-list d-flex flex-wrap">
                            {attachments?.map(renderAttachment)}
                        </ul>
                    </div>
                </section>}
            </div>
        </Modal>
    );
}


// WEBPACK FOOTER //
// ./src/public/pages/BraynTemplateModal.tsx